import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/base/QuoteForm';
import { Link } from 'react-router-dom';

const CostsEstimatesPage = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 sm:pb-16 lg:pb-20 bg-gradient-to-br from-teal-50 via-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              <strong>Gutter Guard Installation Costs</strong> in <strong>Northwest Arkansas</strong>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8">
              Transparent pricing for <strong>gutter protection</strong> in <strong>Fayetteville, Rogers, Bentonville, and Springdale</strong>. Get accurate estimates and understand the factors that affect installation costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a href="tel:479-337-7565" className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all active:scale-95 touch-manipulation shadow-lg whitespace-nowrap min-h-[56px]">
                <i className="ri-phone-line mr-2"></i> Call (479) 337-7565
              </a>
              <a href="/contact" className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-600 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-teal-50 transition-all active:scale-95 touch-manipulation whitespace-nowrap min-h-[56px]">
                Get Free Custom Estimate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Average Costs */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Average <strong>Gutter Guard Installation</strong> Costs
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Prices vary based on home size, gutter type, and product selection. Here are typical ranges for <strong>Northwest Arkansas</strong> homes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg shadow-lg p-6 sm:p-8 border-t-4 border-orange-500">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Basic Protection</h3>
              <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-3 sm:mb-4">$1,200 - $1,800</div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg shadow-lg p-6 sm:p-8 border-t-4 border-teal-500 transform scale-105">
              <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                MOST POPULAR
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Premium Stone Coated</h3>
              <div className="text-3xl sm:text-4xl font-bold text-teal-600 mb-3 sm:mb-4">$2,200 - $3,500</div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg shadow-lg p-6 sm:p-8 border-t-4 border-orange-500">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Complete System</h3>
              <div className="text-3xl sm:text-4xl font-bold text-orange-600 mb-3 sm:mb-4">$3,500 - $5,500</div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Factors */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Factors That Affect <strong>Installation Costs</strong>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-home-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    <strong>Gutter Length</strong>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    The total linear feet of gutters on your home is the primary cost factor. Larger homes with more roofline require more materials and labor.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-product-hunt-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    <strong>Home Size</strong>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Larger homes typically require more extensive gutter systems and installation services, which increases the cost.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-arrow-up-down-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    <strong>System Type</strong>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Different system types have varying costs. Stone coated gutter caps are premium, while basic mesh guards are more affordable.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-tools-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    <strong>Complexity</strong>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Homes with complex rooflines, valleys, or multiple stories require specialized installation techniques and may cost more.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-building-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    <strong>Existing Conditions</strong>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    If your gutters need repair or replacement before installation, this adds to the total cost. We inspect and provide honest recommendations.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                    <strong>Professional Installation</strong>
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Professional installation services add to the overall cost. We offer flexible installation options to fit your schedule.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Comparison */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                <strong>Gutter Guards</strong> vs. Regular Cleaning
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                See how installing <strong>gutter guards</strong> compares to ongoing cleaning costs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gray-50 rounded-lg p-6 sm:p-8 border-2 border-gray-200">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Regular Cleaning (10 Years)</h3>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">$1,200 - $2,400</div>
                <p className="text-sm sm:text-base text-gray-600">
                  <strong>Annual Cost:</strong> $120 - $240<br />
                  <strong>Total Cost:</strong> $1,200 - $2,400
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-6 sm:p-8 border-2 border-teal-500">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  <strong>Gutter Guards</strong> (Lifetime)
                </h3>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">$2,200 - $3,500</div>
                <p className="text-sm sm:text-base text-gray-600">
                  <strong>Annual Cost:</strong> $0<br />
                  <strong>Total Cost:</strong> $2,200 - $3,500
                </p>
                <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg p-4 sm:p-6 text-center">
                  <div className="text-sm font-semibold mb-1">Total Lifetime Savings</div>
                  <div className="text-2xl sm:text-3xl font-bold">$12,000+</div>
                  <div className="text-xs sm:text-sm text-teal-100 mt-2">Plus peace of mind & safety</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
              Get Your Custom Quote Today
            </h2>
            <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 text-teal-50">
              Contact <strong>NWA Gutter Guards</strong> for a free, detailed estimate tailored to your home in <strong>Northwest Arkansas</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a href="tel:479-337-7565" className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all active:scale-95 touch-manipulation shadow-lg whitespace-nowrap min-h-[56px]">
                <i className="ri-phone-line mr-2"></i> Call (479) 337-7565
              </a>
              <a href="/contact" className="inline-flex items-center justify-center bg-white text-teal-600 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-teal-50 transition-all active:scale-95 touch-manipulation whitespace-nowrap min-h-[56px]">
                Request Free Estimate
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CostsEstimatesPage;